import React from 'react';
import { format, getHours, getMinutes } from 'date-fns';
import {
  applyTimeToDate,
  buildCalendarDays,
  buildYearMonths,
  buildYearWindow,
  CALENDAR_PANEL,
  clampTimePart,
  DATE_PICKER_MODE,
  getInitialCalendarPanel,
  getYear,
  getYearWindowLabel,
  isCalendarDayOutsideMonth,
  isSameDay,
  isSameMonth,
  isSameYear,
  isValid,
  MONTH_SHORT_LABELS,
  nextStep,
  padTimePart,
  previousStep,
  setYear,
  shiftCalendarView,
  startOfMonth,
  WEEKDAY_LABELS,
  type CalendarPanel,
  type CalendarViewStep,
  type CustomDatePickerMode,
  type DatePickerRestrictions,
} from './customDatePicker.utils';
import { TimeSpinner, SPINNER_KIND } from './TimeSpinner';
import { useDatePickerRestrictions } from './useDatePickerRestrictions';

const ChevronLeftIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M10 6 8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
);

type CustomDateCalendarProps = {
  viewMonth: Date;
  selectedDate: Date | null;
  mode: CustomDatePickerMode;
  onViewMonthChange: (month: Date) => void;
  onSelectDay: (day: Date) => void;
  onSelectMonth: (month: Date) => void;
  onTimeChange: (hours: number, minutes: number) => void;
  dateRestrictions?: DatePickerRestrictions;
  maxDate?: Date;
  rangeStartDate?: Date | null;
  rangeEndDate?: Date | null;
};

const resolveTimeParts = (
  hoursText: string,
  minutesText: string,
  selectedDate: Date | null
): { hours: number; minutes: number } => {
  const now = new Date();
  const hours =
    clampTimePart(hoursText, 23) ??
    (selectedDate && isValid(selectedDate) ? getHours(selectedDate) : getHours(now));
  const minutes =
    clampTimePart(minutesText, 59) ??
    (selectedDate && isValid(selectedDate) ? getMinutes(selectedDate) : getMinutes(now));
  return { hours, minutes };
};

const toDateOnly = (date: Date) => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const CustomDateCalendar = ({
  viewMonth,
  selectedDate,
  mode,
  onViewMonthChange,
  onSelectDay,
  onSelectMonth,
  onTimeChange,
  dateRestrictions,
  maxDate,
  rangeStartDate,
  rangeEndDate,
}: CustomDateCalendarProps) => {
  const [panel, setPanel] = React.useState<CalendarPanel>(() => getInitialCalendarPanel(mode));

  React.useEffect(() => {
    setPanel(getInitialCalendarPanel(mode));
  }, [mode]);

  const days = React.useMemo(() => buildCalendarDays(viewMonth), [viewMonth]);
  const months = React.useMemo(() => buildYearMonths(viewMonth), [viewMonth]);
  const years = React.useMemo(() => buildYearWindow(viewMonth), [viewMonth]);
  const restrictions = useDatePickerRestrictions(dateRestrictions);

  const [hoursText, setHoursText] = React.useState(() => {
    if (selectedDate && isValid(selectedDate)) return padTimePart(getHours(selectedDate));
    return padTimePart(getHours(new Date()));
  });
  const [minutesText, setMinutesText] = React.useState(() => {
    if (selectedDate && isValid(selectedDate)) return padTimePart(getMinutes(selectedDate));
    return padTimePart(getMinutes(new Date()));
  });
  const [timeInvalid, setTimeInvalid] = React.useState(false);

  React.useEffect(() => {
    if (selectedDate && isValid(selectedDate)) {
      setHoursText(padTimePart(getHours(selectedDate)));
      setMinutesText(padTimePart(getMinutes(selectedDate)));
      setTimeInvalid(false);
    }
  }, [selectedDate]);

  const commitTime = React.useCallback(
    (freshHours?: string, freshMinutes?: string) => {
      const hStr = freshHours ?? hoursText;
      const mStr = freshMinutes ?? minutesText;

      const hours = clampTimePart(hStr, 23);
      const minutes = clampTimePart(mStr, 59);

      if (hours === null || minutes === null) {
        setTimeInvalid(true);
        if (selectedDate && isValid(selectedDate)) {
          setHoursText(padTimePart(getHours(selectedDate)));
          setMinutesText(padTimePart(getMinutes(selectedDate)));
        }
        return;
      }

      setTimeInvalid(false);
      setHoursText(padTimePart(hours));
      setMinutesText(padTimePart(minutes));
      onTimeChange(hours, minutes);
    },
    [hoursText, minutesText, selectedDate, onTimeChange]
  );

  const headerLabel =
    panel === CALENDAR_PANEL.DAYS
      ? format(viewMonth, 'MMMM yyyy')
      : panel === CALENDAR_PANEL.MONTHS
        ? String(getYear(viewMonth))
        : getYearWindowLabel(viewMonth);

  const headerCanDrillDown = panel === CALENDAR_PANEL.DAYS || panel === CALENDAR_PANEL.MONTHS;

  const handleHeaderClick = () => {
    if (panel === CALENDAR_PANEL.DAYS) {
      setPanel(CALENDAR_PANEL.MONTHS);
      return;
    }
    if (panel === CALENDAR_PANEL.MONTHS) {
      setPanel(CALENDAR_PANEL.YEARS);
    }
  };

  const navigateView = (step: CalendarViewStep) => {
    onViewMonthChange(shiftCalendarView(viewMonth, panel, step));
  };

  const handleSelectMonthCell = (month: Date) => {
    const next = startOfMonth(month);
    onViewMonthChange(next);

    if (mode === DATE_PICKER_MODE.MONTH) {
      onSelectMonth(next);
      return;
    }

    setPanel(CALENDAR_PANEL.DAYS);
  };

  const handleSelectYearCell = (yearDate: Date) => {
    onViewMonthChange(setYear(viewMonth, getYear(yearDate)));
    setPanel(CALENDAR_PANEL.MONTHS);
  };

  const handleSelectDayCell = (day: Date) => {
    if (mode !== DATE_PICKER_MODE.DATETIME) {
      onSelectDay(day);
      return;
    }
    const { hours, minutes } = resolveTimeParts(hoursText, minutesText, selectedDate);
    onSelectDay(applyTimeToDate(day, hours, minutes));
  };

  const showTime = mode === DATE_PICKER_MODE.DATETIME && panel === CALENDAR_PANEL.DAYS;

  return (
    <div
      className="MuiDateCalendar-root custom-date-calendar-root"
      role="dialog"
      aria-label={mode === DATE_PICKER_MODE.MONTH ? 'Month picker' : 'Date calendar'}
    >
      <div className="custom-date-calendar-header">
        <button
          type="button"
          className="custom-date-calendar-header__btn"
          aria-label={
            panel === CALENDAR_PANEL.DAYS
              ? 'Previous month'
              : panel === CALENDAR_PANEL.MONTHS
                ? 'Previous year'
                : 'Previous years'
          }
          onClick={() => navigateView(previousStep)}
        >
          <ChevronLeftIcon />
        </button>

        {headerCanDrillDown ? (
          <button
            type="button"
            className="custom-date-calendar-header__label custom-date-calendar-header__label--btn"
            onClick={handleHeaderClick}
            aria-label={panel === CALENDAR_PANEL.DAYS ? 'Select month' : 'Select year'}
          >
            {headerLabel}
          </button>
        ) : (
          <span className="custom-date-calendar-header__label">{headerLabel}</span>
        )}

        <button
          type="button"
          className="custom-date-calendar-header__btn"
          aria-label={
            panel === CALENDAR_PANEL.DAYS
              ? 'Next month'
              : panel === CALENDAR_PANEL.MONTHS
                ? 'Next year'
                : 'Next years'
          }
          onClick={() => navigateView(nextStep)}
        >
          <ChevronRightIcon />
        </button>
      </div>

      <div
        className={`custom-date-calendar-body${showTime ? ' custom-date-calendar-body--with-time' : ''}`}
      >
        {panel === CALENDAR_PANEL.DAYS && (
          <div className="custom-date-day-calendar">
            <div className="custom-date-day-calendar__weekdays" role="row">
              {WEEKDAY_LABELS.map((weekday) => (
                <span
                  key={weekday}
                  className="custom-date-day-calendar__weekday"
                  role="columnheader"
                >
                  {weekday}
                </span>
              ))}
            </div>

            <div className="custom-date-day-calendar__grid" role="grid">
              {days.map((day) => {
                const outside = isCalendarDayOutsideMonth(day, viewMonth);
                const rangeStart = rangeStartDate ? toDateOnly(rangeStartDate).getTime() : null;
                const rangeEnd = rangeEndDate ? toDateOnly(rangeEndDate).getTime() : null;
                const dayTime = toDateOnly(day).getTime();
                const isRangeStart = rangeStart !== null && dayTime === rangeStart;
                const isRangeEnd = rangeEnd !== null && dayTime === rangeEnd;
                const inRange =
                  rangeStart !== null &&
                  rangeEnd !== null &&
                  dayTime > rangeStart &&
                  dayTime < rangeEnd;
                const selected =
                  isRangeStart ||
                  isRangeEnd ||
                  (selectedDate ? isSameDay(day, selectedDate) : false);
                const dateDisabled =
                  (restrictions?.isDisabled(day) ?? false) ||
                  (maxDate !== undefined && toDateOnly(day) > toDateOnly(maxDate));

                return (
                  <button
                    key={`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`}
                    type="button"
                    role="gridcell"
                    aria-selected={selected}
                    className={[
                      'MuiPickersDay-root',
                      'custom-date-day',
                      outside && 'custom-date-day--outside',
                      inRange && 'custom-date-day--in-range',
                      isRangeStart && 'custom-date-day--range-start',
                      isRangeEnd && 'custom-date-day--range-end',
                      selected && 'custom-date-day--selected',
                      dateDisabled && 'custom-date-day--disabled',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onMouseDown={(event) => event.preventDefault()}
                    disabled={dateDisabled}
                    onClick={() => handleSelectDayCell(day)}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {panel === CALENDAR_PANEL.MONTHS && (
          <div className="custom-date-month-grid" role="grid" aria-label="Select month">
            {months.map((month, index) => {
              const selected = selectedDate ? isSameMonth(month, selectedDate) : false;

              return (
                <button
                  key={`${getYear(month)}-${index}`}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  className={[
                    'custom-date-month-cell',
                    selected && 'custom-date-month-cell--selected',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelectMonthCell(month)}
                >
                  {MONTH_SHORT_LABELS[index]}
                </button>
              );
            })}
          </div>
        )}

        {panel === CALENDAR_PANEL.YEARS && (
          <div className="custom-date-year-grid" role="grid" aria-label="Select year">
            {years.map((yearDate) => {
              const year = getYear(yearDate);
              const selected = selectedDate ? isSameYear(yearDate, selectedDate) : false;

              return (
                <button
                  key={year}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  className={[
                    'custom-date-year-cell',
                    selected && 'custom-date-year-cell--selected',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handleSelectYearCell(yearDate)}
                >
                  {year}
                </button>
              );
            })}
          </div>
        )}

        {showTime && (
          <div className="custom-date-calendar-time" aria-label="Time">
            <span className="custom-date-calendar-time__label">Time</span>
            <div className="custom-date-calendar-time__inputs">
              <TimeSpinner
                kind={SPINNER_KIND.HOURS}
                value={hoursText}
                aria-label="Hours"
                invalid={timeInvalid}
                onChange={(val) => {
                  setHoursText(val);
                  setTimeInvalid(false);
                }}
                onCommit={(freshVal) => commitTime(freshVal, minutesText)}
              />
              <span className="custom-date-calendar-time__separator">:</span>
              <TimeSpinner
                kind={SPINNER_KIND.MINUTES}
                value={minutesText}
                aria-label="Minutes"
                invalid={timeInvalid}
                onChange={(val) => {
                  setMinutesText(val);
                  setTimeInvalid(false);
                }}
                onCommit={(freshVal) => commitTime(hoursText, freshVal)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
